import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { patchRoverTdm } from '@/api/Rover/applicationTDM';
import type { IPatchTdmPayload } from '@/api/Rover/applicationTDM/types';
//Custom Components
import Button from '@/components/UI/Button';
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookReactSingleSelect } from '@/components/UI/HookForm/Form/ReactSelect';
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
import Modal from '@/components/UI/Modal';
import { RoverReactQueryKey } from '@/constants/ReactQueryKeys';
//Services
import { notifyUser } from '@/store/Toast';
//Assets and Miscellaneous
import type { ReactSelectOption } from '@/types/Constants';
import type { TdmType } from '@/types/Tdm';
import { sanitizeResponseError } from '@/utils';
import { getTdmStatusList } from '@/utils/project';

const roverQueryKeyMapping = {
  task: RoverReactQueryKey.GET_ROVER_TASKS,
  deliverable: RoverReactQueryKey.GET_ROVER_DELIVERABLES,
  milestone: RoverReactQueryKey.GET_ROVER_MILESTONES,
};
interface IChangeStatusFormProps {
  handleModal: () => void;
  tdmType: TdmType;
  tdm: any;
}

const ChangeStatusForm = ({ handleModal, tdmType, tdm }: IChangeStatusFormProps) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const updateTdm = useMutation(
    (data: Partial<IPatchTdmPayload>) =>
      patchRoverTdm({
        tdmType: tdmType,
        tdmId: tdm?.id,
        data,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([roverQueryKeyMapping[tdmType]]);
        handleModal();
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `${tdmType} updated successfully`,
            toastType: 'success',
          })
        );
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `${tdmType} update failed: ${resError}`,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const onSubmit = (data: { status: ReactSelectOption }) => {
    const payload = { status: data.status.value };
    updateTdm.mutate(payload);
  };

  const StatusList = getTdmStatusList(tdmType);

  let tdmStatusList = Object.keys(StatusList);

  if (tdmType !== 'task') {
    tdmStatusList = tdmStatusList.filter((tdsl: string) => tdsl !== 'UNDER_REVIEW' && tdsl !== 'COMPLETE');
  }

  return (
    <Modal>
      <HookFormProvider
        defaultValues={{
          status: {
            label: StatusList[tdm.status],
            value: tdm.status,
          },
        }}
      >
        <HookForm onSubmit={onSubmit}>
          <h2 className='mt-0'>Change Status</h2>
          <HookReactSingleSelect
            label='Status'
            name='status'
            options={tdmStatusList.map((s) => ({
              label: StatusList[s],
              value: s,
            }))}
          />
          <div className='flex justify-end gap-2 mt-5'>
            <Button label='Cancel' btnStyle='btn-neutral' type='button' onClick={handleModal} />
            <Button
              label={`${updateTdm.isLoading ? 'Saving...' : 'Save'}`}
              type='submit'
              btnStyle='btn-primary'
              disabled={updateTdm.isLoading}
            />
          </div>
        </HookForm>
      </HookFormProvider>
    </Modal>
  );
};

export default ChangeStatusForm;
